import { useState } from "react";
import InModal from "../../controls/InModal";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";
import { getProblemMessage } from "../../utils/http";
import {
  deleteCompanyRewardAssignment,
  deleteDepartmentRewardAssignment,
  deleteUserRewardAssignment,
  deleteCompanyBadge,
  deleteDepartmentBadge,
  deleteUserBadge,
  deleteMaterialCompanyAssignment,
  deleteMaterialDepartmentAssignment,
  deleteMaterialUserAssignment,
} from "../../repository/data.repository";
import {
  AssignmentVariant,
  AssignmentTarget,
} from "./AssignmentGridView.default";

// Delete handler + entity-name field per (variant, target). Re-assignment is done via the bulk assign flow.
const deleteConfig: Record<
  AssignmentVariant,
  { readonly label: string; readonly nameField: string; readonly fns: Record<AssignmentTarget, (uid: string) => Promise<any>> }
> = {
  reward: {
    label: "Reward",
    nameField: "rewardName",
    fns: {
      company: deleteCompanyRewardAssignment,
      department: deleteDepartmentRewardAssignment,
      user: deleteUserRewardAssignment,
    },
  },
  badge: {
    label: "Badge",
    nameField: "badgeName",
    fns: {
      company: deleteCompanyBadge,
      department: deleteDepartmentBadge,
      user: deleteUserBadge,
    },
  },
  material: {
    label: "Material",
    nameField: "materialName",
    fns: {
      company: deleteMaterialCompanyAssignment,
      department: deleteMaterialDepartmentAssignment,
      user: deleteMaterialUserAssignment,
    },
  },
};

export interface AssignmentFormDialogProps {
  readonly open?: boolean;
  readonly variant: AssignmentVariant;
  readonly target: AssignmentTarget;
  readonly initialValues?: any;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function AssignmentFormDialog(props: AssignmentFormDialogProps) {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const cfg = deleteConfig[props.variant];
  const entityName = props.initialValues?.[cfg.nameField];

  function onSubmit(): void {
    if (!props.initialValues?.uid) return;
    setLoading(true);
    setErrorMessage(undefined);
    cfg.fns[props.target](props.initialValues.uid)
      .then(() => {
        setLoading(false);
        props.onFinish?.();
      })
      .catch((err) => {
        setLoading(false);
        setErrorMessage(getProblemMessage(err));
      });
  }

  function onCancel(): void {
    setErrorMessage(undefined);
    props.onCancel?.();
  }

  return (
    <InModal
      open={props.open}
      size="small"
      title={`Remove ${cfg.label} Assignment`}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode="delete"
          loading={loading}
          onCancel={onCancel}
          onSubmit={onSubmit}
          submitText="Remove"
        />
      }
    >
      <ModalDeleteConfirmation
        entityLabel={`${cfg.label.toLowerCase()} assignment`}
        entityName={entityName}
        verb="removing"
      />
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default AssignmentFormDialog;
